import type { Mindset } from './client';
import type { CoachAssignment } from './coachAssignment';
import type { Company, CompanyFeatureFlags } from './company';
import type { UserEmployment } from './userEmployment';
import type { UserNotifications } from './userNotifications';

interface GrowthPlanSummary {
  id: string;
  northStar: string;
  oneYearVision: string;
  started: boolean;
}

export interface UserContractEnd {
  date: string | null;
  displayMode: 'contract_end' | 'ongoing' | null;
  source: 'hubspot';
  status: string | null;
}

export interface User {
  activeChemCalls?: number;
  adminNote?: string;
  avatarUrl?: string;
  b2bEnabled?: boolean;
  b2b_enabled?: boolean;
  mfaRequirementDisabled?: boolean;
  momentsBaseUrl?: string;
  coachAssignments?: CoachAssignment[];
  coachProfile?: CoachProfile;
  coacheeAssignments?: CoachAssignment[];
  coachees: User[];
  coaches: User[];
  cometChatId: string;
  // MEN-4160: member's current company on the session's nested user
  // (UserBlueprint :session_participant view).
  company?: { id: string; name: string } | null;
  createdAt: string; // Timestamp
  contractEnd?: UserContractEnd | null;
  currentCompany?: Partial<Company>;
  autoShareSessionTranscripts: boolean;
  insightsV2Enabled?: boolean;
  documentsEmail?: string;
  email: string;
  emailUpdated: boolean;
  firstName: string;
  folderId?: string;
  fullName: string;
  growthPlans: GrowthPlanSummary[];
  hasCoaches: boolean;
  hasMaxDocuments: boolean;
  id: string;
  insightsStatus?: 'unsent' | 'in_progress' | 'shared' | 'unknown';
  isActive: boolean;
  isBusinessPaid: boolean;
  isPaid: boolean;
  lastLogin?: string; // Timestamp
  lastName: string;
  mindset: Mindset;
  memberStatus?: string;
  loginRequirements?: {
    required_auth_method?: string;
  };
  membersCount?: number;
  profile?: UserProfile;
  roles?: Array<Role | string>;
  permissionsHash?: string;
  featureFlags?: CompanyFeatureFlags;
  sessionTimeoutLength: number;
  starters: string[];
  sub: string;
  updatedAt: string; // Timestamp
  userAssesment?: UserAssesment;
  userEmployments?: UserEmployment[];
  userNotifications?: UserNotifications;
  userPayment?: UserPayment;
}

// Note: This mirrors the `status` enum in the BE UserPayment model. Updates here should match there.
export enum UserPaymentStatus {
  active = 'active',
  canceled = 'canceled',
  incomplete = 'incomplete',
  paused = 'paused',
}

export enum SeatType {
  standard_leadership = 'standard_leadership',
  executive = 'executive',
}

export interface UserProfile {
  bio?: string;
  company?: string;
  createdAt: string; // Timestamp
  experience: string;
  linkedIn: string;
  location: string;
  matchPreference?: string;
  notifyEmail: boolean;
  notifySms: boolean;
  phone?: string;
  pronouns?: string;
  scheduleMatchCall?: string; // Timestamp
  seatType?: SeatType;
  timezone?: string | null;
  title?: string;
  updatedAt: string; // Timestamp
  userId: string;
}

export interface CoachCertification {
  name: string;
  earnedOn: string;
}

export interface CoachProfile {
  id: string;
  bannerDismissedUntil?: string; // Timestamp
  bragSheet: string | null;
  calendarUrl?: string;
  schedulingProvider?: 'cal' | 'moments';
  capacity: number;
  certifications?: CoachCertification[];
  executiveTrainingCompleted?: boolean;
  gender?: string | null;
  // Coach lifecycle dates
  startDate?: string | null; // Timestamp (YYYY-MM-DD or ISO)
  terminationDate?: string | null; // Timestamp (YYYY-MM-DD or ISO)
  vacationEndDate?: string | null;
  vacationStartDate?: string | null;
  // Seat rates keyed by SeatType
  seatTypeRates?: Partial<Record<SeatType, number | null>>;
  focus: string;
  functions?: string[];
  industries: Array<string>;
  role: Array<string>;
  styles: Array<string>;
  subFunctions?: string[];
  user: User;
  userId: string;
}

export interface UserPayment {
  createdAt: string; // Timestamp
  customerId: string;
  plan?: string;
  status: UserPaymentStatus;
  subscriptionId: string;
  updatedAt: string; // Timestamp
}

export interface UserAssesment {
  careerGoals: string[];
  challenges: string[];
  coachingNeeds: string[];
  complete?: boolean;
  dreamJobs: string[];
  interests: string[];
  motivations: string[];
}

export interface AboutForm {
  company: string;
  experience: string;
  firstName: string;
  lastName: string;
  title: string;
  linkedIn: string;
}

export interface Role {
  name: string;
  resourceType?: string | null;
  resourceName?: string | null;
  // Resource-scoped roles can include a resourceId (e.g., Company id)
  resourceId?: string | null;
}

// RBAC helper functions
export const hasRole = (
  user: User,
  roleName: string,
  resourceType?: string,
  resourceName?: string,
): boolean => {
  if (!user.roles || user.roles.length === 0) return false;

  return user.roles.some((role) => {
    if (typeof role === 'string') {
      return (
        role === roleName &&
        resourceType === undefined &&
        resourceName === undefined
      );
    }

    if (role.name !== roleName) return false;
    if (resourceType !== undefined && role.resourceType !== resourceType)
      return false;
    if (resourceName !== undefined && role.resourceName !== resourceName)
      return false;

    return true;
  });
};

export const hasGlobalRole = (user: User, roleName: string): boolean =>
  user.roles?.some(
    (role) =>
      (typeof role === 'string' && role === roleName) ||
      (typeof role !== 'string' &&
        role.name === roleName &&
        (role.resourceType === undefined || role.resourceType === null) &&
        (role.resourceId === undefined || role.resourceId === null)),
  ) ?? false;

export const isAdmin = (user: User): boolean => hasRole(user, 'admin');

// Ops who may use the platform admin calendar and admin scheduling surfaces:
// a global admin or super_admin, as Moments' CanAccessPlatformAdminCalendar.
export const canAccessPlatformAdminCalendar = (user: User): boolean =>
  hasGlobalRole(user, 'admin') || hasGlobalRole(user, 'super_admin');

export const isCoach = (user: User): boolean => hasRole(user, 'coach');

export const isSupport = (user: User): boolean => hasRole(user, 'support');

// Company admin helpers
export const isCompanyAdmin = (user: User): boolean =>
  hasRole(user, 'company_admin');

export const getCompanyAdminCompanyIds = (user: User): string[] => {
  if (!user.roles || user.roles.length === 0) return [];
  return user.roles
    .filter(
      (role) =>
        typeof role !== 'string' &&
        role.name === 'company_admin' &&
        role.resourceType === 'Company' &&
        !!role.resourceId,
    )
    .map((role) => (role as Role).resourceId as string);
};

export const hasFeature = (user: User, feature: string): boolean =>
  user.featureFlags?.[feature] !== false;
